// ========= START: src/components/GameSetupScreen.js (COMPLETE REPLACEMENT) =========
import React, { useState, useMemo, useEffect, useRef } from 'react';
import vocabulary from '../data/pronunciation_vocab';

const CATEGORIES = [
  { id: 'feelings', name: 'Feelings', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_feelings.png` },
  { id: 'verbs', name: 'Verbs', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_verbs.png` },
  { id: 'colors', name: 'Colors', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_colors.png` },
  { id: 'animals', name: 'Animals', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_animals.png` },
  { id: 'fruit', name: 'Fruit', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_fruit.png` },
  { id: 'vegetables', name: 'Vegetables', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_vegetables.png` },
  { id: 'food', name: 'Food', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_food.png` },
  { id: 'numbers', name: 'Numbers', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_numbers.png` },
  { id: 'family', name: 'Family', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_family.png` },
  { id: 'body', name: 'Body', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_body.png` },
  { id: 'shapes', name: 'Shapes', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_shapes.png` },
  { id: 'sports', name: 'Sports', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_sports.png` },
  { id: 'stationery', name: 'Stationery', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_stationery.png` },
  { id: 'school_places', name: 'School Places', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_school_places.png` },
  { id: 'school_subjects', name: 'School Subjects', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_school_subjects.png` },
  { id: 'school_events', name: 'School Events', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_school_events.png` },
  { id: 'club_activities', name: 'Club Activities', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_club_activities.png` },
  { id: 'daily_activities', name: 'Daily Activities', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_daily_activities.png` },
  { id: 'hobbies', name: 'Hobbies', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_hobbies.png` },
  { id: 'musical_instruments', name: 'Musical Instruments', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_musical_instruments.png` },
  { id: 'jobs', name: 'Jobs', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_jobs.png` },
  { id: 'weather', name: 'Weather', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_weather.png` },
  { id: 'seasons', name: 'Seasons', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_seasons.png` },
  { id: 'days', name: 'Days', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_days.png` },
  { id: 'months', name: 'Months', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_months.png` },
  { id: 'time_expressions', name: 'Time Expressions', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_time_expressions.png` },
  { id: 'frequency_adverbs', name: 'Frequency Adverbs', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_frequency_adverbs.png` },
  { id: 'nature', name: 'Nature', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_nature.png` },
  { id: 'places', name: 'Places', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_places.png` },
  { id: 'countries', name: 'Countries', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_countries.png` },
  { id: 'transportation', name: 'Transportation', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_transportation.png` },
  { id: 'japanese_events', name: 'Japanese Events', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_japanese_events.png` },
  { id: 'items', name: 'Items', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_items.png` },
  { id: 'adjectives', name: 'Adjectives', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_adjectives.png` },
  { id: 'concepts', name: 'Concepts', image: `${process.env.PUBLIC_URL}/assets/images/categories/category_concepts.png` },
];

const GRADE_LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const WORD_COUNT_OPTIONS = [10, 20, 30, 40, 50];

const displayGrade = (grade) => {
  if (grade > 6) {
    return `JHS ${grade - 6}`;
  }
  return `G${grade}`;
};

function GameSetupScreen({ gameSettings, onGameSettingsChange, onStartGame }) {
  const [isPlayButtonHovered, setIsPlayButtonHovered] = useState(false);

  // Ref for the PARENT container (the fixed space)
  const pickerContainerRef = useRef(null);

  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [gridStyles, setGridStyles] = useState({ gridTemplateColumns: 'repeat(5, 1fr)', buttonSize: 100 });

  const availableCategories = useMemo(() => {
    const effectiveGrade = gameSettings.grade;
    const categoriesForGrade = new Set(
      vocabulary
        .filter(word => word.grade <= effectiveGrade)
        .map(word => word.category)
    );
    return CATEGORIES.map(cat => ({
      ...cat,
      isAvailable: categoriesForGrade.has(cat.id),
    }));
  }, [gameSettings.grade]);

  // 1. Measure the AVAILABLE space in the parent container
  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      if (entries[0]) {
        // We use contentRect of the PARENT wrapper
        const { width, height } = entries[0].contentRect;
        // Subtract padding manually if needed, though contentRect usually handles inner size
        setContainerSize({ width: width - 30, height: height - 60 }); // -30 for padding, -60 for header text
      }
    });

    const currentRef = pickerContainerRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, []);

  // 2. Calculate the Best Grid Layout
  useEffect(() => {
    if (containerSize.width <= 0 || containerSize.height <= 0 || availableCategories.length === 0) return;

    const calculateBestLayout = () => {
      const categoryCount = availableCategories.length;
      const { width, height } = containerSize;
      const gap = 15;
      let bestLayout = { cols: 4, buttonSize: 0 }; // Default fallback

      // Iterate through reasonable column counts (e.g., 2 to 12)
      for (let cols = 2; cols <= 12; cols++) {
        if (cols > categoryCount) break; // Don't have more columns than items

        const rows = Math.ceil(categoryCount / cols);

        // Calculate available space minus gaps
        const totalGapWidth = Math.max(0, cols - 1) * gap;
        const totalGapHeight = Math.max(0, rows - 1) * gap;

        // Space available for a single button
        const widthPerSlot = (width - totalGapWidth) / cols;
        const heightPerSlot = (height - totalGapHeight) / rows;

        // Button is square, so limit by the smaller dimension
        const buttonSize = Math.floor(Math.min(widthPerSlot, heightPerSlot));

        // We want the layout that produces the *largest* buttons
        if (buttonSize > bestLayout.buttonSize) {
          bestLayout = { cols, buttonSize };
        }
      }

      // Safety clamp
      const finalButtonSize = Math.min(Math.max(bestLayout.buttonSize, 60), 200);

      setGridStyles({
        gridTemplateColumns: `repeat(${bestLayout.cols}, ${finalButtonSize}px)`,
        buttonSize: finalButtonSize,
      });
    };
    calculateBestLayout();
  }, [containerSize, availableCategories]);

  const handleCategoryToggle = (categoryId, isAvailable) => {
    if (!isAvailable) return;
    const newCategories = [...gameSettings.categories];
    const index = newCategories.indexOf(categoryId);

    if (index === -1) {
      newCategories.push(categoryId);
    } else {
      newCategories.splice(index, 1);
    }
    onGameSettingsChange({ ...gameSettings, categories: newCategories });
  };

  // UPDATED: Handle grade change by preserving valid categories
  const handleGradeChange = (newGrade) => {
    // 1. Determine which categories are valid for the NEW grade
    const categoriesForNewGrade = new Set(
      vocabulary
        .filter(word => word.grade <= newGrade)
        .map(word => word.category)
    );

    // 2. Filter currently selected categories to keep only those valid for the new grade
    const preservedCategories = gameSettings.categories.filter(catId =>
      categoriesForNewGrade.has(catId)
    );

    onGameSettingsChange({
      ...gameSettings,
      grade: newGrade,
      categories: preservedCategories
    });
  };

  const handleStartClick = () => {
    if (gameSettings.categories.length === 0) {
      alert('Please select at least one category!');
      return;
    }
    onStartGame();
  };

  return (
    <div className="game-setup-screen">
      <div className="setup-top-row">
        {/* Grade Section */}
        <div className="setup-section setup-section-grade">
          <h2 className="setup-header-text">Choose Grade Level</h2>
          <div className="grade-level-container">
            {GRADE_LEVELS.map(grade => (
              <button
                key={grade}
                className={`word-count-button ${gameSettings.grade === grade ? 'selected' : ''}`}
                onClick={() => handleGradeChange(grade)}>
                {displayGrade(grade)}
              </button>
            ))}
          </div>
        </div>

        {/* Word Count Section */}
        <div className="setup-section setup-section-words">
          <h2 className="setup-header-text">How many words?</h2>
          <div className="word-count-container">
            {WORD_COUNT_OPTIONS.map(count => (
              <button
                key={count}
                className={`word-count-button ${gameSettings.wordCount === count ? 'selected' : ''}`}
                onClick={() => onGameSettingsChange({ ...gameSettings, wordCount: count })}>
                {count}
              </button>
            ))}
          </div>
        </div>

        {/* Play Button */}
        <button
          className="play-button"
          onClick={handleStartClick}
          onMouseEnter={() => setIsPlayButtonHovered(true)}
          onMouseLeave={() => setIsPlayButtonHovered(false)}
        >
          <img
            src={isPlayButtonHovered ? `${process.env.PUBLIC_URL}/assets/menu-buttons/button_play_hover.gif` : `${process.env.PUBLIC_URL}/assets/menu-buttons/button_play.png`}
            alt="Play Game"
            className="play-button-image"
          />
        </button>
      </div>

      {/* Category Picker (Ref goes on this outer wrapper now) */}
      <div className="setup-section category-picker" ref={pickerContainerRef}>
        <h2 className="setup-header-text">Choose Categories (Select one or more)</h2>
        <div
          className="category-container"
          style={{
            gridTemplateColumns: gridStyles.gridTemplateColumns,
            justifyContent: 'center',
            alignContent: 'start'
          }}
        >
          {availableCategories.map(category => {
            const isSelected = gameSettings.categories.includes(category.id);
            let buttonClass = 'category-button';
            if (isSelected) buttonClass += ' selected';
            if (!category.isAvailable) buttonClass += ' disabled';

            return (
              <button
                key={category.id}
                className={buttonClass}
                onClick={() => handleCategoryToggle(category.id, category.isAvailable)}
                disabled={!category.isAvailable}
                style={{ width: `${gridStyles.buttonSize}px`, height: `${gridStyles.buttonSize}px` }}
              >
                <img src={category.image} alt={category.name} className="category-image" />
                <span className="category-text" style={{ fontSize: `${Math.max(10, gridStyles.buttonSize * 0.11)}px` }}>
                  {category.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default GameSetupScreen;
// ========= END: src/components/GameSetupScreen.js (COMPLETE REPLACEMENT) =========